import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1625948050673-05a31a2f79c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbmF0dXJlJTIwbGVhdmVzJTIwbWFjcm98ZW58MHx8fHwxNzg0MTM5NDEwfDA&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.5 + i * 0.12 },
  }),
};

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} initial="hidden" animate="show" custom={i} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.25, 0.85]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen w-full overflow-hidden flex items-end">
      {/* Parallax background */}
      <motion.div style={{ y }} className="absolute inset-0 -z-20">
        <img src={HERO_BG} alt="" className="w-full h-[120%] object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-brand-ink" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-brand-ink via-brand-ink/50 to-brand-ink/10" />
      {/* Soft orange glow for depth */}
      <div
        className="absolute -z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/3 w-[80vw] h-[60vh] rounded-full blur-[120px] opacity-30"
        style={{ background: "radial-gradient(circle, #E9531A 0%, transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto w-full px-6 md:px-10 pb-24 md:pb-32 pt-32">
        <motion.img
          src="/clarkes-logomark.png"
          alt="Clarkes GroundCare — Professional Ground Maintenance Services"
          data-testid="hero-logo"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="w-[240px] md:w-[360px] h-auto mb-10 drop-shadow-2xl"
        />

        <h1 className="font-display uppercase leading-[0.82] tracking-tight text-white text-[13vw] md:text-[10vw] lg:text-[8.5rem] max-w-6xl">
          <MaskLine i={0}>Let&apos;s keep your</MaskLine>
          <MaskLine i={1} className="text-brand-orange">grounds looking their best</MaskLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="max-w-md text-white/75 text-base md:text-lg font-body leading-relaxed"
          >
            Reliable lawn care, hedge work and garden maintenance across the majority of the
            North East of England — for homes, landlords and businesses.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-3 px-7 py-4 bg-brand-orange text-white font-semibold hover:bg-brand-orangeHover transition-colors"
            >
              Get a Free Quote
              <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
            </button>
            <button
              data-testid="hero-view-services"
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              className="px-7 py-4 border border-white/25 text-white font-medium hover:border-brand-orange hover:text-brand-orange transition-colors"
            >
              Our Services
            </button>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <span className="relative w-px h-10 bg-white/20 overflow-hidden">
          <motion.span
            className="absolute top-0 left-0 w-px h-4 bg-brand-orange"
            animate={{ y: [-16, 40] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </span>
      </motion.div>
    </section>
  );
};
